import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { capitalCallCreateSchema } from "features/capital-call/CapitalCallSchemas";
import {
  CapitalCallFeeSelectCellRenderer,
  CapitalCallInvestmentSelectCellRenderer,
  CapitalCallManagementFeeAmountCellRenderer,
  CapitalCallManagementFeeFundFamilyAmountCellRenderer,
  CapitalCallNotesCellRenderer,
  CapitalCallTransactionSelectCellRenderer,
} from "features/capital-call/drawdown/CapitalCallDrawdownFields";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import { getCapitalActivityEventRetrieveQueryKey } from "gen/endpoints/capital-activity/capital-activity";
import {
  getCapitalCallFeeBasisNotificationTableDataRetrieveQueryKey,
  useCapitalCallCreate,
  useCapitalCallRetrieveSuspense,
} from "gen/endpoints/capital-call/capital-call";
import { useFeeBreakDistributionExistsRetrieve } from "gen/endpoints/fee-break-distribution-exists/fee-break-distribution-exists";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import {
  getFundFamilyEventRetrieveQueryKey,
  getFundFamilySubEventListQueryKey,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyUseAllocationTableData } from "gen/model/fundFamilyUseAllocationTableData";
import { FundFamilyUseAllocationTableDef } from "gen/model/fundFamilyUseAllocationTableDef";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { cloneDeep } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import { fundFamilyUseAllocationTableResolver } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableFormUtils";
import {
  FundFamilyUseAllocationTableAmountCellRendererSelector,
  FundFamilyUseAllocationTableColumnCellRendererSelector,
  FundFamilyUseAllocationTableGetAllocationFn,
  FundFamilyUseAllocationTableOnRowAddFn,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { useFundFamilyUseAllocationTableFormRowController } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableRowController";
import { useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import invariant from "shared/utils/TinyInvariant";

const CATEGORY_IDS_WITH_INVESTMENT = ["investment", "expenses"];

export interface CapitalCallDrawdownFormProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  eventData: FundFamilyEventData;
}

export const CapitalCallDrawdownForm: React.FC<
  CapitalCallDrawdownFormProps
> = ({ fundFamilyId, fundFamilyEventId, eventData }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["capitalCall", "common"]);

  const { data: initialData } = useCapitalCallRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
    {
      query: {
        gcTime: 0,
        staleTime: 0,
        select: useCallback(
          // react-hook-form mutates `data` object, which is frozen, so we need to clone it
          (data: FundFamilyUseAllocationTableData) => cloneDeep(data),
          [],
        ),
      },
    },
  );

  const form = useForm<FundFamilyUseAllocationTableData>({
    resolver,
    defaultValues: initialData,
  });

  const { rows, onRowChange, addRow, onRowRemove } =
    useFundFamilyUseAllocationTableFormRowController(form, "rows");

  const { navigateNext } = usePageWizard();

  const {
    mutateAsync: createCapitalCall,
    variables: createVariables,
    error: createError,
    reset: createReset,
  } = useCapitalCallCreate({
    mutation: {
      onSuccess: async () => {
        await invalidateQueryKeys(
          queryClient,
          getCapitalActivityEventRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
          ),
          getFundFamilySubEventListQueryKey(fundFamilyId, fundFamilyEventId),
        );
      },
    },
  });

  const onSubmit = async (data: FundFamilyUseAllocationTableData) => {
    await createCapitalCall({
      fundFamilyId,
      fundFamilyEventId,
      data,
    });

    await queryClient.invalidateQueries({
      queryKey: getFundFamilyEventRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    });
    await invalidateQueryKeys(
      queryClient,
      getCapitalCallFeeBasisNotificationTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    );
  };

  const { data: funds } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });

  const allocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    useMemo(
      () =>
        rows.map((row) => {
          invariant(row.column_data?.type === "capital_call");
          return {
            transaction_code_id:
              row.column_data.use_of_funds.transaction_code_id || null,
            investment_id: row.column_data.investment_id || null,
          };
        }),
      [rows],
    ),
    eventData.effective_date,
  );

  // Disable calling for management fees if there has been a fee break distribution in the fund family
  const { data: feeBreakDistributionExists } =
    useFeeBreakDistributionExistsRetrieve(fundFamilyId);

  const getAllocation =
    useCallback<FundFamilyUseAllocationTableGetAllocationFn>(
      (params) => {
        const columnData = params.row.column_data;
        if (params.row.category_id === "management_fees") {
          return undefined;
        }
        if (columnData?.type === "capital_call") {
          const fundAllocations = allocationsByLookupKey?.({
            transaction_code_id:
              columnData.use_of_funds.transaction_code_id || null,
            investment_id: columnData.investment_id || null,
          });
          const fundInfo = fundAllocations?.find(
            (it) => it.id === params.fundDef.fund_id,
          );
          if (!fundInfo) {
            return undefined;
          }
          return {
            ownershipPercentage: fundInfo.ownershipPercentage,
            allocationRuleId: fundInfo.allocationRuleId,
            allocationRuleName: fundInfo.allocationRuleName,
          };
        }
        return undefined;
      },
      [allocationsByLookupKey],
    );

  const tableDef = useMemo<FundFamilyUseAllocationTableDef>(
    () => ({
      enable_row_add_remove: true,
      show_total_row: true,
      column_data_type: "capital_call",
      total_title: t("common:total"),
      headers: [
        {
          id: "use_of_funds",
          title: t("capitalCall:drawdown.columns.useOfFunds.title"),
          width: 300,
        },
        {
          id: "investment_id",
          title: t("capitalCall:drawdown.columns.investment.title"),
          width: 300,
        },
      ],
      trailers: [
        {
          id: "notes",
          title: "Notes",
          width: 300,
        },
      ],
      fund_family: {
        fund_family_id: fundFamilyId,
        title: t("capitalCall:drawdown.columns.fundFamily.title"),
      },
      funds: funds.map((fund) => ({
        fund_id: fund.id!,
        title: fund.legal_name,
      })),
      categories: [
        {
          id: "management_fees",
          title: t("capitalCall:drawdown.categories.managementFees.title"),
          allow_auto_allocate: true,
          allow_multiple: true,
          is_disabled:
            feeBreakDistributionExists?.fee_break_distribution_exists,
          disabled_reason: t(
            "capitalCall:drawdown.categories.managementFees.disabledReason",
          ),
        },
        {
          id: "investment",
          title: t("capitalCall:drawdown.categories.investment.title"),
          allow_auto_allocate: true,
          allow_multiple: true,
        },
        {
          id: "expenses",
          title: t("capitalCall:drawdown.categories.expenses.title"),
          allow_auto_allocate: true,
          allow_multiple: true,
        },
        {
          id: "taxes",
          title: t("capitalCall:drawdown.categories.taxes.title"),
          allow_auto_allocate: true,
          allow_multiple: true,
        },
        {
          id: "capital_reserves",
          title: t("capitalCall:drawdown.categories.capitalReserve.title"),
          allow_auto_allocate: true,
          allow_multiple: true,
        },
        {
          id: "bridge_financing",
          title: t("capitalCall:drawdown.categories.bridgeFinancing.title"),
          allow_auto_allocate: true,
          allow_multiple: true,
        },
      ],
      amount_types: [
        {
          id: "called",
        },
      ],
    }),
    [fundFamilyId, funds, feeBreakDistributionExists, t],
  );

  const getErrorForCell =
    useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse(
      createError,
      useCallback(
        (rowId) =>
          createVariables?.data.rows.findIndex((it) => it.id === rowId),
        [createVariables],
      ),
    );

  const onRowAdd = useCallback<FundFamilyUseAllocationTableOnRowAddFn>(
    (data) => {
      const newRow: FundFamilyUseAllocationTableRow = cloneDeep(data);
      newRow.column_data = {
        type: "capital_call",
        use_of_funds: {},
        investment_id: undefined,
        notes: "",
      };
      if (newRow.category_id === "management_fees") {
        for (const amountTypes of Object.values(newRow.funds)) {
          for (const amountType of Object.values(amountTypes)) {
            amountType.meta = {
              type: "management_fee",
              fee_charge_event_id_to_call_details: {},
              total_fees_to_call: undefined,
            };
          }
        }
      }
      addRow(newRow);
    },
    [addRow],
  );

  return (
    <Form
      form={form}
      onBeforeSubmit={createReset}
      onSubmit={onSubmit}
      onSuccess={navigateNext}
      isDisabled={eventData.state !== "draft"}
      excludeNonFieldErrors={["rows"]}
      onError={() => true}
    >
      <FormErrorAlert
        error={createError}
        excludeNonFieldErrors={["rows"]}
        mb="16px"
      />
      <TableBox isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxActions />
        </TableBoxHeader>
        <TableBoxBody>
          <FundFamilyUseAllocationTable
            onRowAdd={onRowAdd}
            onRowRemove={onRowRemove}
            onRowChange={onRowChange}
            getAllocation={getAllocation}
            amountCellRendererSelector={amountCellRendererSelector}
            columnCellRendererSelector={columnCellRendererSelector}
            tableDef={tableDef}
            tableData={{ rows }}
            getErrorForCell={getErrorForCell}
          />
        </TableBoxBody>
      </TableBox>
      <PageWizardFormFooter />
    </Form>
  );
};

const resolver = fundFamilyUseAllocationTableResolver(
  zodResolver(capitalCallCreateSchema),
);

const amountCellRendererSelector: FundFamilyUseAllocationTableAmountCellRendererSelector =
  (params) => {
    const categoryId = params.row.category_id;
    if (categoryId === "management_fees") {
      if (
        "fund_id" in params.fundFamilyOrFundDef &&
        !params.row.is_auto_allocate
      ) {
        return CapitalCallManagementFeeAmountCellRenderer;
      }
      if (
        "fund_family_id" in params.fundFamilyOrFundDef &&
        params.row.is_auto_allocate
      ) {
        return CapitalCallManagementFeeFundFamilyAmountCellRenderer;
      }
    }
  };

const columnCellRendererSelector: FundFamilyUseAllocationTableColumnCellRendererSelector =
  (columnDef, params) => {
    const categoryId = params.data?.category_id;
    if (columnDef.id === "use_of_funds") {
      return categoryId === "management_fees"
        ? CapitalCallFeeSelectCellRenderer
        : CapitalCallTransactionSelectCellRenderer;
    } else if (
      columnDef.id === "investment_id" &&
      categoryId &&
      CATEGORY_IDS_WITH_INVESTMENT.includes(categoryId)
    ) {
      return CapitalCallInvestmentSelectCellRenderer;
    } else if (columnDef.id === "notes") {
      return CapitalCallNotesCellRenderer;
    }
    return undefined;
  };
